import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { useToast } from "@/hooks/use-toast";
import { LeadershipPanel } from "./LeadershipPanel";
import {
  Users, UserPlus, CheckCircle2, XCircle, Clock, Hash,
  Search, Filter, Loader2, ShieldCheck, ShieldOff, AlertCircle,
  BarChart3, FileText, Award, Building2, Crown, Network,
} from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";

const STATUS_COLORS: Record<string, string> = {
  active:    "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending:   "bg-amber-100 text-amber-700 border-amber-200",
  suspended: "bg-red-100 text-red-700 border-red-200",
  rejected:  "bg-gray-100 text-gray-600 border-gray-200",
};

function memberInitials(name: string) {
  return name.split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();
}

function generateMembershipNumber(assocPrefix: string, sequence: number): string {
  const year = new Date().getFullYear();
  const seq  = String(sequence).padStart(4, "0");
  const pfx  = (assocPrefix || "NJA").toUpperCase().slice(0, 4);
  return `${pfx}-${year}-${seq}`;
}

// ─── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="p-5 flex items-center gap-4">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="text-sm text-muted-foreground mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function AssociationDashboard() {
  const { orgId } = useOrgPermissions();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatus]   = useState("all");
  const [selectedMember, setSelected] = useState<any | null>(null);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", beats: "", news_org: "", country_code: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setCurrentUserId(user?.id ?? null));
  }, []);

  // ── Load association record for this org ──────────────────────────────────
  const { data: assoc } = useQuery({
    queryKey: ["my-association", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("associations" as any)
        .select("id, name, acronym, country_code")
        .eq("org_id", orgId!)
        .maybeSingle() as any);
      return data as any;
    },
  });

  // ── Current president ─────────────────────────────────────────────────────
  const { data: president } = useQuery({
    queryKey: ["assoc-president", assoc?.id],
    enabled: !!assoc?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_leadership" as any)
        .select(`
          id, member_id, tenure_start,
          association_positions!inner(title),
          association_members!inner(journalist_profiles(full_name, email, avatar_url))
        `)
        .eq("association_id", assoc!.id)
        .eq("is_current", true)
        .eq("association_positions.title", "President")
        .maybeSingle() as any);
      return data as any;
    },
  });

  const presidentProfile = president?.association_members?.journalist_profiles;
  const isPresident = !!(currentUserId && president && president.association_members);
  // Note: we check president_user_id via the leadership row's user_id column
  // Since user_id is stored in leadership rows, check if current user matches
  const { data: myLeadership } = useQuery({
    queryKey: ["my-leadership", assoc?.id, currentUserId],
    enabled: !!assoc?.id && !!currentUserId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_leadership" as any)
        .select("id, association_positions(title, permissions)")
        .eq("association_id", assoc!.id)
        .eq("user_id", currentUserId!)
        .eq("is_current", true)
        .maybeSingle() as any);
      return data as any;
    },
  });
  const amPresident = myLeadership?.association_positions?.title === "President";
  const canAssignLeadership = amPresident || !!(myLeadership?.association_positions?.permissions as any)?.can_assign_leadership;

  // ── Members ───────────────────────────────────────────────────────────────
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["assoc-members", assoc?.id],
    enabled: !!assoc?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from("association_members" as any)
        .select(`
          *,
          journalist_profiles(full_name, email, phone, account_type, avatar_url, bio, headline)
        `)
        .eq("association_id", assoc!.id)
        .order("created_at", { ascending: false }) as any);
      return (data ?? []) as any[];
    },
  });

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = {
    total:     members.length,
    active:    members.filter(m => m.status === "active").length,
    pending:   members.filter(m => m.status === "pending").length,
    suspended: members.filter(m => m.status === "suspended").length,
  };

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = members.filter(m => {
    const name = (m.journalist_profiles?.full_name ?? "").toLowerCase();
    const email = (m.journalist_profiles?.email ?? "").toLowerCase();
    const num   = (m.membership_number ?? "").toLowerCase();
    const q     = search.toLowerCase();
    const matchSearch = !q || name.includes(q) || email.includes(q) || num.includes(q);
    const matchStatus = statusFilter === "all" || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── Approve member (assign membership number) ────────────────────────────
  const approveMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const activeCount = members.filter(m => m.status === "active").length;
      const num = generateMembershipNumber(assoc?.acronym ?? "NJA", activeCount + 1);
      const { error } = await (supabase
        .from("association_members" as any)
        .update({
          status:            "active",
          membership_number: num,
          approved_at:       new Date().toISOString(),
        })
        .eq("id", memberId) as any);
      if (error) throw error;
      return num;
    },
    onSuccess: (num) => {
      toast({ title: "Member approved", description: `Membership number ${num} assigned.` });
      qc.invalidateQueries({ queryKey: ["assoc-members", assoc?.id] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const suspendMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await (supabase
        .from("association_members" as any)
        .update({ status: "suspended", suspended_at: new Date().toISOString() })
        .eq("id", memberId) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Member suspended." });
      qc.invalidateQueries({ queryKey: ["assoc-members", assoc?.id] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await (supabase
        .from("association_members" as any)
        .update({ status: "active", suspended_at: null, suspension_reason: null })
        .eq("id", memberId) as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Member reactivated." });
      qc.invalidateQueries({ queryKey: ["assoc-members", assoc?.id] });
    },
  });

  // ── Onboard new member manually ───────────────────────────────────────────
  const handleOnboard = async () => {
    if (!form.full_name.trim() || !form.email.trim() || !assoc?.id) return;
    setSubmitting(true);
    try {
      // Create journalist_profile if not exists (by email lookup)
      const { data: existing } = await (supabase
        .from("journalist_profiles" as any)
        .select("id")
        .eq("email", form.email.trim())
        .maybeSingle() as any);

      let jpId = existing?.id;
      if (!jpId) {
        const { data: newJp, error: jpErr } = await (supabase
          .from("journalist_profiles" as any)
          .insert({
            id:           crypto.randomUUID(),
            full_name:    form.full_name.trim(),
            email:        form.email.trim(),
            phone:        form.phone.trim() || null,
            account_type: "standard",
          })
          .select("id")
          .single() as any);
        if (jpErr) throw jpErr;
        jpId = newJp.id;
      }

      // Create association_members record in pending state
      const { error: memErr } = await (supabase
        .from("association_members" as any)
        .insert({
          association_id: assoc.id,
          journalist_id:  jpId,
          status:         "pending",
          applied_at:     new Date().toISOString(),
          beats:          form.beats ? form.beats.split(",").map(b => b.trim()).filter(Boolean) : [],
          news_org:       form.news_org.trim() || null,
          country_code:   form.country_code.trim() || null,
        }) as any);
      if (memErr) throw memErr;

      toast({ title: "Member onboarded", description: "Application created. Approve to assign membership number." });
      setForm({ full_name: "", email: "", phone: "", beats: "", news_org: "", country_code: "" });
      setOnboardOpen(false);
      qc.invalidateQueries({ queryKey: ["assoc-members", assoc.id] });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!assoc && !isLoading) {
    return (
      <Card>
        <CardContent className="py-16 flex flex-col items-center gap-3 text-center">
          <Building2 className="w-10 h-10 text-muted-foreground" />
          <p className="font-semibold">No association linked to this organisation yet.</p>
          <p className="text-sm text-muted-foreground">An association record needs to be created by a super admin first.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">{assoc?.name ?? "Association"}</h1>
          <p className="text-muted-foreground mt-1">
            Member management · <span className="font-medium">{assoc?.acronym}</span>
          </p>
        </div>
        <Sheet open={onboardOpen} onOpenChange={setOnboardOpen}>
          <SheetTrigger asChild>
            <Button className="gap-2">
              <UserPlus className="w-4 h-4" />
              Onboard Member
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Onboard New Member</SheetTitle>
            </SheetHeader>
            <div className="space-y-4 mt-6">
              <div>
                <Label>Full Name *</Label>
                <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Jane Doe" />
              </div>
              <div>
                <Label>Email Address *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jane@outlet.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+234..." />
              </div>
              <div>
                <Label>News Organisation / Outlet</Label>
                <Input value={form.news_org} onChange={e => setForm(f => ({ ...f, news_org: e.target.value }))} placeholder="Daily Tribune" />
              </div>
              <div>
                <Label>Country Code</Label>
                <Input value={form.country_code} onChange={e => setForm(f => ({ ...f, country_code: e.target.value }))} placeholder="NG" maxLength={2} />
              </div>
              <div>
                <Label>Beats (comma-separated)</Label>
                <Input value={form.beats} onChange={e => setForm(f => ({ ...f, beats: e.target.value }))} placeholder="Politics, Economy, Sports" />
              </div>
              <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3 flex gap-2">
                <Hash className="w-4 h-4 shrink-0 mt-0.5" />
                A unique membership number will be automatically assigned when you approve this member.
              </p>
              <Button className="w-full" onClick={handleOnboard} disabled={submitting || !form.full_name || !form.email}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create Application
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* President card + stats row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* President card — spans 2 cols */}
        <Card className="md:col-span-2 border-[#C8A84B]/40 bg-[#0D2240]/5">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#0D2240] flex items-center justify-center text-[#F0E4B8] text-sm font-bold shrink-0">
              {presidentProfile?.avatar_url
                ? <img src={presidentProfile.avatar_url} className="w-full h-full rounded-full object-cover" alt="" />
                : (presidentProfile?.full_name ?? "?").split(" ").slice(0, 2).map((s: string) => s[0]).join("").toUpperCase()
              }
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Crown className="w-3.5 h-3.5 text-[#C8A84B]" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">President</span>
              </div>
              {presidentProfile ? (
                <>
                  <p className="font-bold text-sm leading-tight">{presidentProfile.full_name}</p>
                  <p className="text-xs text-muted-foreground">{presidentProfile.email}</p>
                  {president?.tenure_start && (
                    <p className="text-xs text-muted-foreground mt-0.5">Since {new Date(president.tenure_start).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">No president assigned</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats — 3 cols */}
        <StatCard icon={Users}         label="Total Members"  value={stats.total}     color="bg-blue-100 text-blue-600" />
        <StatCard icon={CheckCircle2}  label="Active"         value={stats.active}    color="bg-emerald-100 text-emerald-600" />
        <StatCard icon={Clock}         label="Pending"        value={stats.pending}   color="bg-amber-100 text-amber-600" />
      </div>

      {/* Member list */}
      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members"><Users className="w-4 h-4 mr-1.5" />Members</TabsTrigger>
          <TabsTrigger value="leadership"><Crown className="w-4 h-4 mr-1.5" />Leadership</TabsTrigger>
          <TabsTrigger value="overview"><BarChart3 className="w-4 h-4 mr-1.5" />Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="space-y-4 mt-4">
          {/* Search + filter */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by name, email or membership #"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatus}>
              <SelectTrigger className="w-36">
                <Filter className="w-4 h-4 mr-1.5" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <ConorbitLoader variant="inline" />
          ) : filtered.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {members.length === 0 ? "No members yet. Onboard your first journalist." : "No members match your filter."}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Membership #</TableHead>
                    <TableHead>Outlet / Beats</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(m => {
                    const jp   = m.journalist_profiles;
                    const name = jp?.full_name ?? "Unknown";
                    return (
                      <TableRow key={m.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setSelected(m)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                              {memberInitials(name)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{name}</p>
                              <p className="text-xs text-muted-foreground">{jp?.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {m.membership_number ? (
                            <span className="font-mono text-xs bg-muted px-2 py-1 rounded flex items-center gap-1 w-fit">
                              <Hash className="w-3 h-3" />{m.membership_number}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Pending approval</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{m.news_org ?? "—"}</p>
                          {m.beats?.length > 0 && (
                            <p className="text-xs text-muted-foreground">{m.beats.slice(0, 2).join(", ")}{m.beats.length > 2 ? "…" : ""}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border ${STATUS_COLORS[m.status] ?? "bg-muted text-muted-foreground"}`} variant="outline">
                            {m.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {m.applied_at ? new Date(m.applied_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1 justify-end">
                            {m.status === "pending" && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                  onClick={() => approveMutation.mutate(m.id)} disabled={approveMutation.isPending}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />Approve
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={() => suspendMutation.mutate(m.id)}>
                                  <XCircle className="w-3 h-3 mr-1" />Reject
                                </Button>
                              </>
                            )}
                            {m.status === "active" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                                onClick={() => suspendMutation.mutate(m.id)}>
                                <ShieldOff className="w-3 h-3 mr-1" />Suspend
                              </Button>
                            )}
                            {m.status === "suspended" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                onClick={() => reactivateMutation.mutate(m.id)}>
                                <ShieldCheck className="w-3 h-3 mr-1" />Reinstate
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="leadership" className="mt-4">
          {assoc?.id ? (
            <LeadershipPanel
              assocId={assoc.id}
              currentUserId={currentUserId}
              isPresident={amPresident}
            />
          ) : (
            <div className="text-center py-10 text-muted-foreground text-sm">No association linked.</div>
          )}
        </TabsContent>

        <TabsContent value="overview" className="mt-4">
          <AssociationOverview members={members} assoc={assoc} />
        </TabsContent>
      </Tabs>

      {/* Member detail sheet */}
      {selectedMember && (
        <MemberDetailSheet
          member={selectedMember}
          onClose={() => setSelected(null)}
          onApprove={(id) => approveMutation.mutate(id)}
          onSuspend={(id) => suspendMutation.mutate(id)}
          onReinstate={(id) => reactivateMutation.mutate(id)}
          assocId={assoc?.id}
        />
      )}
    </div>
  );
}

// ─── Member detail sheet ──────────────────────────────────────────────────────
function MemberDetailSheet({ member, onClose, onApprove, onSuspend, onReinstate, assocId }: {
  member: any;
  onClose: () => void;
  onApprove: (id: string) => void;
  onSuspend: (id: string) => void;
  onReinstate: (id: string) => void;
  assocId: string;
}) {
  const jp = member.journalist_profiles;
  const name = jp?.full_name ?? "Unknown";

  return (
    <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent className="w-full max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Member Details</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
              {memberInitials(name)}
            </div>
            <div>
              <p className="text-lg font-semibold">{name}</p>
              <p className="text-sm text-muted-foreground">{jp?.email}</p>
              {member.membership_number && (
                <span className="inline-flex items-center gap-1 font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                  <Hash className="w-3 h-3" />{member.membership_number}
                </span>
              )}
            </div>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2">
            <Badge className={`border ${STATUS_COLORS[member.status]}`} variant="outline">
              {member.status}
            </Badge>
            {member.suspended_at && (
              <span className="text-xs text-muted-foreground">Suspended {new Date(member.suspended_at).toLocaleDateString()}</span>
            )}
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow label="Phone"    value={jp?.phone ?? "—"} />
            <InfoRow label="Outlet"   value={member.news_org ?? "—"} />
            <InfoRow label="Country"  value={member.country_code ?? "—"} />
            <InfoRow label="Applied"  value={member.applied_at ? new Date(member.applied_at).toLocaleDateString() : "—"} />
            <InfoRow label="Approved" value={member.approved_at ? new Date(member.approved_at).toLocaleDateString() : "—"} />
            <InfoRow label="Type"     value={jp?.account_type ?? "—"} />
          </div>

          {member.beats?.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Beats</p>
              <div className="flex flex-wrap gap-1.5">
                {member.beats.map((b: string) => (
                  <Badge key={b} variant="secondary" className="text-xs">{b}</Badge>
                ))}
              </div>
            </div>
          )}

          {jp?.bio && (
            <div>
              <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Bio</p>
              <p className="text-sm">{jp.bio}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap pt-2 border-t">
            {member.status === "pending" && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { onApprove(member.id); onClose(); }}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />Approve &amp; Assign Number
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => { onSuspend(member.id); onClose(); }}>
                  <XCircle className="w-4 h-4 mr-1.5" />Reject
                </Button>
              </>
            )}
            {member.status === "active" && (
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" onClick={() => { onSuspend(member.id); onClose(); }}>
                <ShieldOff className="w-4 h-4 mr-1.5" />Suspend Member
              </Button>
            )}
            {member.status === "suspended" && (
              <Button size="sm" variant="outline" className="text-blue-600 border-blue-200" onClick={() => { onReinstate(member.id); onClose(); }}>
                <ShieldCheck className="w-4 h-4 mr-1.5" />Reinstate Member
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="font-medium mt-0.5">{value}</p>
    </div>
  );
}

// ─── Association overview / analytics ────────────────────────────────────────
function AssociationOverview({ members, assoc }: { members: any[]; assoc: any }) {
  const byCountry = members.reduce((acc: Record<string, number>, m) => {
    const c = m.country_code ?? "Unknown";
    acc[c] = (acc[c] ?? 0) + 1;
    return acc;
  }, {});

  const allBeats = members.flatMap(m => m.beats ?? []);
  const byBeat = allBeats.reduce((acc: Record<string, number>, b: string) => {
    acc[b] = (acc[b] ?? 0) + 1;
    return acc;
  }, {});
  const topBeats = (Object.entries(byBeat) as [string, number][]).sort((a, b) => b[1] - a[1]).slice(0, 6);

  const recentApprovals = members
    .filter(m => m.status === "active" && m.approved_at)
    .sort((a, b) => new Date(b.approved_at).getTime() - new Date(a.approved_at).getTime())
    .slice(0, 5);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader><CardTitle className="text-sm">Membership by Country</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(byCountry).length === 0 ? (
            <p className="text-sm text-muted-foreground">No data yet.</p>
          ) : (Object.entries(byCountry) as [string, number][]).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
            <div key={c} className="flex items-center gap-2">
              <span className="text-sm font-medium w-8">{c}</span>
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${(n / Math.max(members.length, 1)) * 100}%` }} />
              </div>
              <span className="text-sm text-muted-foreground w-6 text-right">{n}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Top Beats</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {topBeats.length === 0 ? (
            <p className="text-sm text-muted-foreground">No beats recorded yet.</p>
          ) : topBeats.map(([beat, n]) => (
            <div key={beat} className="flex items-center gap-2">
              <span className="text-sm flex-1 truncate">{beat}</span>
              <Badge variant="secondary" className="text-xs">{n}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader><CardTitle className="text-sm">Recently Approved Members</CardTitle></CardHeader>
        <CardContent>
          {recentApprovals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No approvals yet.</p>
          ) : (
            <div className="space-y-2">
              {recentApprovals.map(m => {
                const jp = m.journalist_profiles;
                const name = jp?.full_name ?? "Unknown";
                return (
                  <div key={m.id} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {memberInitials(name)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{name}</p>
                      <p className="text-xs text-muted-foreground">{jp?.email}</p>
                    </div>
                    <span className="font-mono text-xs bg-muted px-2 py-1 rounded flex items-center gap-1">
                      <Hash className="w-3 h-3" />{m.membership_number}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(m.approved_at).toLocaleDateString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
